import type { ErechnungFormat, SteuerAufschluesselungZeile, XmlValidierung } from '../types';

/** Anzeigename des E-Rechnungs-Formats; null, wenn das Dokument keine E-Rechnung ist. */
export function formatLabel(format?: string | null): string | null {
  switch (format) {
    case 'ZUGFERD': return 'E-Rechnung · ZUGFeRD';
    case 'XRECHNUNG_CII': return 'E-Rechnung · XRechnung (CII)';
    case 'XRECHNUNG_UBL': return 'E-Rechnung · XRechnung (UBL)';
    default: return null;
  }
}

export function istErechnungFormat(format?: string | null): format is ErechnungFormat {
  return formatLabel(format) !== null;
}

/** BT-3 381 (Gutschrift) und 384 (Rechnungskorrektur) werden mit negativen Summen geführt. */
export function istGutschrift(typCode?: string | null): boolean {
  return typCode === '381' || typCode === '384';
}

export function gutschriftLabel(typCode?: string | null): string {
  return typCode === '384' ? 'Gutschrift (Korrektur)' : 'Gutschrift';
}

const KATEGORIE_TEXT: Record<string, string> = {
  S: 'Regelsteuersatz',
  Z: 'Nullsatz',
  E: 'Steuerbefreit',
  AE: 'Reverse Charge',
  K: 'Innergemeinschaftlich',
  G: 'Ausfuhr',
  O: 'Nicht steuerbar',
  L: 'Kanarische Inseln (IGIC)',
  M: 'Ceuta/Melilla (IPSI)',
};

export function kategorieText(code?: string | null): string {
  if (!code) return '–';
  const text = KATEGORIE_TEXT[code.toUpperCase()];
  return text ? `${code} · ${text}` : code;
}

function alsZahl(wert?: string | number | null): number | null {
  if (wert === null || wert === undefined || wert === '') return null;
  const n = typeof wert === 'number' ? wert : Number(wert);
  return Number.isFinite(n) ? n : null;
}

export function satzText(satz?: string | number | null): string {
  const n = alsZahl(satz);
  return n === null ? '–' : `${n.toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;
}

export function betragText(wert?: string | number | null): string {
  const n = alsZahl(wert);
  return n === null
    ? '–'
    : `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/** Beträge fehlen komplett, wenn der Preisfilter sie entfernt hat (kein Recht "Preise sehen"). */
export function hatSteuerBetraege(zeilen: SteuerAufschluesselungZeile[]): boolean {
  return zeilen.some(z => z.basis != null || z.betrag != null);
}

export interface ValidierungAnzeige {
  klasse: 'ok' | 'fehler' | 'ungeprueft';
  titel: string;
  hinweis: string;
  meldungen: string[];
}

/** Aufbereitung der XML-Prüfung für die Anzeige; null, wenn keine Prüfung vorliegt. */
export function validierungAnzeige(v?: XmlValidierung | null): ValidierungAnzeige | null {
  if (!v || !v.status) return null;
  const meldungen = Array.isArray(v.meldungen) ? v.meldungen : [];
  switch (v.status) {
    case 'OK':
      return {
        klasse: 'ok',
        titel: 'XML geprüft',
        hinweis: 'Die E-Rechnungs-XML ist formal in Ordnung.',
        meldungen,
      };
    case 'FEHLER':
      return {
        klasse: 'fehler',
        titel: 'Formatfehler in der E-Rechnung',
        hinweis:
          'Das ist ein Formatproblem auf Seiten des Lieferanten. Die Rechnung wurde trotzdem '
          + 'angelegt – bitte inhaltlich prüfen und bei Bedarf eine korrigierte E-Rechnung anfordern.',
        meldungen,
      };
    case 'UNGEPRUEFT':
      return {
        klasse: 'ungeprueft',
        titel: 'Nicht vollständig geprüft',
        hinweis:
          'Die XML wurde gelesen, aber nicht gegen das Schema geprüft (z. B. bei XRechnung UBL '
          + 'nur strukturell). Das ist kein Hinweis auf einen Fehler.',
        meldungen,
      };
    default:
      return null;
  }
}
